import {
  isInternalHostname,
  normalizeHostname
} from "../../../../shared/network/internal-hostname";
import { isPrivateIpv4, isPrivateIpv6 } from "../../../../shared/network/private-ip";

/**
 * SSRF guard for tenant-supplied webhook URLs. The backend POSTs signed
 * payloads to such URLs server-side, so they must never point into the
 * internal network: http(s) only, and no loopback / private / link-local /
 * internal-suffix hosts. The WHATWG URL parser normalizes obfuscated IPv4
 * forms (decimal/hex/octal) to dotted-quad before the range checks run.
 *
 * This is the WRITE boundary and it is not the one that binds. Delivery dials
 * through the backend's egress core (`_common/network/resolve-safe-target.ts`),
 * which reads the SAME range table from `shared/network/private-ip` and then does
 * the thing no string check can: it resolves the name itself, judges every
 * address it answers with, and pins the socket to those addresses. A hand-synced
 * backend COPY of this function used to be that re-check; it had drifted once
 * already (NEX-3548) and is gone.
 *
 * 🔴 **SO THIS IS THE STRING-DECIDABLE PREFIX OF THE DIAL'S OWN RULE, AND IT MUST
 * STAY EXACTLY THAT — A GROUND THE DIAL REFUSES AND THIS ACCEPTS IS NOT A LENIENT
 * WRITE BOUNDARY, IT IS A ROW THAT SAVES 2xx AND FAILS ON EVERY DELIVERY.** There
 * is no error for the customer to read at the moment they can still act on it;
 * the only evidence is a FAILED run nobody is watching. `judgeTargetUrl` cannot be
 * imported here — this package is framework-free and that one is backend — so the
 * two are separate implementations of one judgement, and the single thing holding
 * them together is
 * `apps/backend/src/_common/network/v1-webhook-write-door-agrees-with-the-dial.spec.ts`.
 */
export function isSafeWebhookUrl(rawUrl: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return false;
  }
  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return false;

  // `https://trusted.example@evil.example/x` parses with hostname `evil.example`,
  // so this is NOT the parse defence — `new URL` already settled the authority.
  // It refuses the credential section outright, for the reason the dial does: a
  // URL carrying one is never a shape this backend should be dialling on a
  // tenant's behalf. `judgeTargetUrl` throws `SsrfRefusedError("credentials")`
  // here, before it resolves anything, so without this line the write door
  // accepts a target that can never be delivered to.
  if (parsed.username !== "" || parsed.password !== "") return false;

  // Trailing-dot FQDNs ("localhost.") resolve identically — normalize first.
  // Both halves come from `shared/network/internal-hostname` now, so this
  // boundary copy and the backend's delivery-time copy cannot drift on a
  // hostname the way the copies had drifted on the IP ranges (NEX-3548) and
  // the way this one and the custom-model schema had drifted on names
  // (NEX-3943).
  const hostname = normalizeHostname(parsed.hostname);
  if (isInternalHostname(hostname)) {
    return false;
  }
  // IPv6 literals keep their brackets in URL.hostname.
  if (hostname.startsWith("[") && hostname.endsWith("]")) {
    return !isPrivateIpv6(hostname.slice(1, -1));
  }
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) {
    return !isPrivateIpv4(hostname);
  }
  return true;
}
